import proxy from '@/config/proxy';
import type { ParamsType } from '@/config/proxy';
import { getCookie } from '@/utils/cookie';
import { isApp, isFlutter } from '@/utils/tools';
import { pinia } from '@/store/index';
import { useUserStore } from '@/store/User';
import { getLanguage } from '@/lang/i18n';
import { useCrypto } from '@/utils/crypto';
import { checkUserExp } from '@/utils/user-exp';
import { slowRequestManager } from '@/utils/slowRequestManager';

const userStore = useUserStore(pinia);

interface FetchConfig extends RequestInit {
  url: string;
  crypto?: boolean;
  responseHandle?: (data: any, response: Response) => any;
  requestStartTime?: number;
  params?: Record<string, any>;
}

interface FetchResponse<T = any> {
  success: boolean;
  code?: number;
  message?: string;
  data: T;
  msgArgs?: any[];
}

interface CustomHeaders {
  [key: string]: string | undefined;
}

const matchConfig = (config: FetchConfig): ParamsType => {
  const cache: ParamsType = {
    url: config.url || ''
  };

  Object.keys(proxy).some(key => {
    if (cache.url.indexOf(key) === 0) {
      const { url, headers = {}, params, responseHandle } = proxy[key];

      cache.url = cache.url.replace(key, url);
      cache.headers =
        typeof headers === 'function' ? headers(config.headers) : headers;
      
      // 处理 params
      if (typeof params === 'function') {
        cache.params = params(config.params);
      } else if (params) {
        cache.params = params;
      }
      
      cache.responseHandle =
        typeof responseHandle === 'function' ? responseHandle : undefined;

      return true;
    }

    return false;
  });
  return cache;
};

/**
 * 请求参数处理
 */
const requestHandle = (config: FetchConfig): FetchConfig => {
  // 记录请求开始时间
  config.requestStartTime = Date.now();

  const { url, headers, params, responseHandle } = matchConfig(config);
  config.url = url;
  config.responseHandle = responseHandle;

  const headersObj: CustomHeaders = {
    'Accept-Language': getLanguage().toLowerCase()
  };

  const { isTg, isH5 } = usePlat();
  
  // 设置平台相关 headers
  if (isFlutter() || isH5) {
    headersObj.Platform = 'h5';
    headersObj.osType = 'h5';
  }
  if (isTg) {
    headersObj.Platform = 'tg';
  }

  // 设置默认 Content-Type
  if (config.method === 'POST' && !headersObj['Content-Type']) {
    headersObj['Content-Type'] = 'application/json';
  }

  // 处理 headers
  if (headers) {
    Object.assign(headersObj, headers);
  }

  // 处理 URL 参数
  const urlParams = new URLSearchParams({
    r: Math.random().toString(32).substring(2),
    __test__: window.location.href,
    c_token: getCookie('c_token') || '',
    Platform: isApp() ? 'h5' : ''
  });

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      urlParams.append(key, String(value));
    });
  }

  // 添加 URL 参数
  const urlWithParams = `${url}?${urlParams.toString()}`;

  // 处理加密
  if (config.crypto && import.meta.env.VITE_CRYPTO_SWITCH !== 'close') {
    const { addCryptoHeaders } = useCrypto();
    Object.assign(headersObj, addCryptoHeaders(config));
  }

  return {
    ...config,
    url: urlWithParams,
    headers: headersObj as any
  };
};

/**
 * 响应数据处理
 */
const responseHandle = async(response: Response, config: FetchConfig): Promise<FetchResponse> => {
  try {
    // 计算接口耗时并追踪
    const requestTime = Date.now() - (config.requestStartTime || 0);
    slowRequestManager.trackRequest(requestTime, config);
  } catch (error) {
    console.log('Error tracking request:', error);
  }

  const data = await response.json();

  const newData: FetchResponse = {
    success: data && data.code ? data.code === 200 : response.status === 200,
    code: data && data.code ? data.code : response.status,
    message: data && data.msg ? data.msg : (data.message || data.error || 'success'),
    data: data && data.data ? data.data : data,
    msgArgs: data?.msgArgs || []
  };

  checkUserExp(response);

  if (config.responseHandle) {
    return config.responseHandle(newData, response);
  }

  return newData;
};

/**
 * 错误处理
 */
const errorHandle = (error: any): FetchResponse => {
  if (error?.request?.responseURL?.indexOf('/user/get_base_info') <= 0
    && error?.response?.data?.code === 30000
  ) {
    if (!isApp()) {
      userStore.logOut();
    }
  }

  return {
    success: false,
    code: (error?.response?.status && String(error.response.status).startsWith('5')) 
      ? error.response.status 
      : (error?.response?.data?.code || 500),
    data: null,
    message: error?.response?.data?.msg || 'Access error, please try again later',
    msgArgs: error.response?.data?.msgArgs || []
  };
};

/**
 * 封装的 fetch 请求函数
 */
const customFetch = async <T = any>(config: FetchConfig): Promise<FetchResponse<T>> => {
  try {
    const processedConfig = requestHandle(config);
    const response = await fetch(processedConfig.url, {
      method: processedConfig.method || 'GET',
      headers: processedConfig.headers,
      body: processedConfig.body,
      credentials: 'include',
      keepalive: processedConfig.keepalive
    });

    return await responseHandle(response, processedConfig);
  } catch (error) {
    return errorHandle(error);
  }
};

export default customFetch; 